import { Box, xcss } from '@atlaskit/primitives';
import Button from '@atlaskit/button/new';
import { useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import Anchor from '@atlaskit/primitives/anchor';
import { FormattedMessage } from 'react-intl';
import { useInteractionContext } from '@atlaskit/react-ufo/interaction-context';
import ErrorSearchNoResultIllustration from './illustrations/ErrorSearchNoResultIllustration';
import { messages } from './messages';
import { ANALYTICS_SCREEN_NAMES } from '../analytics/analyticsEvents';
import { useSendAnalyticEvent } from '../analytics/analyticsClient';
import { SitesWithRovoEnabledResult, UserAccount } from '../clients/gateway-client/types';
import { GatewayQueryKeys } from '../clients/gateway-client/gatewayClient';
import { isNarrowWindow } from '../utils/isNarrowWindow';
import ProfileOnlyTopBarLayout from './navigation/ProfileOnlyTopBarLayout';

const errorStyles = xcss({
	marginTop: 'space.800',
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'center',
	textWrap: 'wrap',
	textAlign: 'center',
});

const textStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'center',
	rowGap: 'space.050',
	marginBottom: 'space.200',
});

const errorMessageStyles = xcss({
	font: 'font.heading.xxlarge',
	fontWeight: 'font.weight.medium',
});

const subtextStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'center',
	rowGap: 'space.250',
	font: 'font.body',
});

const linkStyles = xcss({
	color: 'color.text',
	textDecoration: 'underline',
	':hover': {
		textDecoration: 'none',
		color: 'color.text',
	},
});

const RovoDisabledError = ({
	userAccount,
	metadata,
	showNavigation = false,
}: {
	userAccount?: UserAccount;
	metadata?: SitesWithRovoEnabledResult['metadata'];
	showNavigation?: boolean;
}): React.JSX.Element => {
	const sendAnalyticEvent = useSendAnalyticEvent();
	const queryClient = useQueryClient();
	const [isLoading, setIsLoading] = useState(false);
	const ufoContext = useInteractionContext();

	const handleReload = async (): Promise<void> => {
		setIsLoading(true);
		const releaseHold = ufoContext?.hold('rovo-disabled-reload');
		try {
			await queryClient.invalidateQueries({
				queryKey: [GatewayQueryKeys.SEARCH_SITES_WITH_ROVO_ENABLED_WITH_METADATA_NEW],
			});
		} finally {
			setIsLoading(false);
			releaseHold?.();
		}
	};

	// https://data-portal.internal.atlassian.com/analytics/registry/87408
	useEffect(() => {
		sendAnalyticEvent({
			eventType: 'screen',
			event: {
				name: ANALYTICS_SCREEN_NAMES.EXTENSION_NO_ROVO_VIEW,
				attributes: {
					...metadata,
				},
			},
			userId: userAccount?.accountId,
		});
	}, [sendAnalyticEvent, userAccount?.accountId, metadata]);

	const errorContent = (
		<Box xcss={errorStyles}>
			<ErrorSearchNoResultIllustration />
			<Box xcss={textStyles}>
				<Box xcss={errorMessageStyles}>
					<FormattedMessage {...messages.rovoDisabledError} />
				</Box>
				<Box xcss={subtextStyles}>
					<Box>
						<FormattedMessage {...messages.rovoDisabledErrorSubheader} />
					</Box>
					<Anchor href="https://www.atlassian.com/software/rovo" target="_parent" xcss={linkStyles}>
						<FormattedMessage {...messages.rovoDisabledErrorLink} />
					</Anchor>
				</Box>
			</Box>
			<Button appearance="primary" isLoading={isLoading} onClick={handleReload}>
				<FormattedMessage {...messages.reloadButtonLabel} />
			</Button>
		</Box>
	);

	const shouldShowHeader = showNavigation && userAccount?.accountId && !isNarrowWindow();
	return shouldShowHeader ? (
		<ProfileOnlyTopBarLayout
			site={undefined}
			testId="rovo-disabled-top-navigation"
			userAccount={userAccount}
		>
			{errorContent}
		</ProfileOnlyTopBarLayout>
	) : (
		errorContent
	);
};

export default RovoDisabledError;
